import { Suspense, lazy, useEffect } from "react";
import ProtectedRoute from './components/auth/ProtectedRoute';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { seoConfig } from '@/utils/seoConfig';
import { setSEOMeta } from '@/utils/seoUtils';
import Layout from './Layout'
const HomePage = lazy(() => import('./pages/HomePage'));
const FriendsPage = lazy(() => import('./pages/FriendsPage'));
const BetaPage = lazy(() => import('./pages/BetaPage'));
const FeedbackDetailPage = lazy(() => import('./pages/FeedbackDetailPage'));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const ProjectsPage = lazy(() => import('./pages/projects/ProjectsPage'));
const NewProjectPage = lazy(() => import('./pages/projects/NewProjectPage'));
const ProjectCompletionPage = lazy(() => import('./pages/projects/ProjectCompletionPage'));
const SingleMessagesPage = lazy(() => import('./pages/SingleMessagesPage'));
const SingleProjectPage = lazy(() => import('./pages/projects/SingleProjectPage/index'));
const ProjectJoinRequestsPage = lazy(() => import('./pages/projects/ProjectJoinRequestsPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ExplorePage = lazy(() => import('./pages/ExplorePage'));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'));
const LibraryPage = lazy(() => import('./pages/LibraryPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const RemixPage = lazy(() => import('./pages/RemixPage'));
const ComingSoonPage = lazy(() => import('./pages/ComingSoonPage'));
const SharePage = lazy(() => import('./pages/SharePage'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const OnboardingPage = lazy(() => import('./pages/auth/OnboardingPage'));
const EditProfilePage = lazy(() => import('./pages/EditProfilePage'));
const DesignSystemPage = lazy(() => import('./pages/DesignSystemPage'));

const LoadingSpinner = (options: {
  hasBackground: boolean
}) => {
  return (
    <div className={`flex items-center justify-center h-screen${options.hasBackground ? ' bg-linear-to-b from-zinc-900 to-black p-4' : ''}`}>
      <div className="w-12 h-12 border-4 border-[rgb(var(--accent-1))] border-t-transparent rounded-full animate-spin"></div>
    </div>
  );
}

/*
IMPORTANT: Summarize all app routes here

AUTHENTICATION ROUTES:
- /login - User login page
- /register - User registration page
- /onboarding - Onboarding flow (protected)

MAIN APP ROUTES (under Layout):
Homepage:
- / - Home/dashboard page

Explore:
- /explore - Discover and explore content

Projects (most are protected):
- /projects - Projects listing page
- /projects/new - Create new project
- /projects/:id - View single project
- /projects/:projectId/manage-requests - Manage project join requests
- /projects/:projectId/complete - Project completion flow
- /projects/edit/:id - Edit existing project
- /projects/:id/invite - Invite friends to project (coming soon)

Commissions (coming soon):
- /commissions - Browse commissions
- /commissions/create - Create new commission
- /commissions/:id - View commission details
- /commissions/:id/submit - Submit to commission
- /my-commissions - User's active commissions
- /my-submissions - User's commission submissions

Library:
- /library - User's personal library

Friends (protected):
- /friends - Friends management page
- /friends/posts - Friends activity feed (coming soon)

Notifications:
- /notifications - User notifications

Remix:
- /remix - Remix/discover creative projects

Messages:
- /messages - Messages overview (coming soon)
- /messages/:id - Individual conversation

Profile:
- /profile - Current user's profile (protected)
- /profile/:id - View other user's profile
- /profile/edit - Edit current user's profile (protected)

Settings:
- /settings - User settings page

Beta Testing (protected):
- /beta-testing - Beta features access
- /feedback/:id - Detailed feedback view

Events (coming soon):
- /events - Browse events
- /events/create - Create new event
- /events/:id - View event details

Utility:
- /design-system - Design system documentation
- /share - Share content/page
- * (catch-all) - 404/Page not found

*/

function Router() {
  return (
    <BrowserRouter>
      <AppRouter />
    </BrowserRouter>
  );
}

function AppRouter() {
  const location = useLocation();

  useEffect(() => {
    const seo = seoConfig[location.pathname];
    if (seo) {
      setSEOMeta(seo);
    }
  }, [location.pathname]);

  return (
    <Routes>
        {/* Authentication Routes */}
        <Route path='/login' element={
          <Suspense fallback={<LoadingSpinner hasBackground={true} />}>
            <LoginPage />
          </Suspense>
        } />
        <Route path='/register' element={
          <Suspense fallback={<LoadingSpinner hasBackground={true} />}>
            <RegisterPage />
          </Suspense>
        } />
        <Route
          path='/onboarding'
          element={
            <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            </Suspense>
          }
        />
        {/* -------------------------- Layout Start: -------------------------- */}
        <Route path="/" element={<Layout />}>
          {/* Homepage: */}
          <Route
            index
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <HomePage />
              </Suspense>
            }
          />
          {/* Explore: */}
          <Route
            path='/explore'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ExplorePage />
              </Suspense>
            }
          />
          {/* Projects: */}
          <Route
            path='/projects'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ProtectedRoute>
                  <ProjectsPage />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path='/projects/new'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ProtectedRoute>
                  <NewProjectPage />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path='/projects/:id'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <SingleProjectPage />
              </Suspense>
            }
          />
          <Route
            path='/projects/:projectId/manage-requests'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ProtectedRoute>
                  <ProjectJoinRequestsPage />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path='/projects/:projectId/complete'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ProtectedRoute>
                  <ProjectCompletionPage />
                </ProtectedRoute>
              </Suspense>
            }
          />
          {/* Commissions: */}
          <Route
            path='/commissions'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ComingSoonPage feature="Commissions" />
              </Suspense>
            }
          />
          <Route
            path='/commissions/create'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ComingSoonPage feature="Commission Creation" />
              </Suspense>
            }
          />
          <Route
            path='/commissions/:id'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ComingSoonPage feature="Commission Details" />
              </Suspense>
            }
          />
          <Route
            path='/commissions/:id/submit'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ComingSoonPage feature="Commission Submission" />
              </Suspense>
            }
          />
          <Route
            path='/my-commissions'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ComingSoonPage feature="My Commissions" />
              </Suspense>
            }
          />
          <Route
            path='/my-submissions'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ComingSoonPage feature="My Submissions" />
              </Suspense>
            }
          />
          {/* Library: */}
          <Route
            path='/library'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <LibraryPage />
              </Suspense>
            }
          />
          {/* Friends: */}
          <Route
            path='/friends'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ProtectedRoute>
                  <FriendsPage />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path='/friends/posts'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ComingSoonPage feature="Friends Posts" />
              </Suspense>
            }
          />
          {/* Notifications: */}
          <Route
            path='/notifications'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <NotificationsPage />
              </Suspense>
            }
          />
          <Route
            path='/remix'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <RemixPage />
              </Suspense>
            }
          />
          {/* Messages: */}
          <Route
            path='/messages'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ComingSoonPage feature="Messages Overview" />
              </Suspense>
            }
          />
          <Route
            path='/messages/:id'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <SingleMessagesPage />
              </Suspense>
            }
          />
          {/* Profile: */}
          <Route
            path='/profile'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path='/profile/:id'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <UserProfilePage />
              </Suspense>
            }
          />
          <Route
            path='/profile/edit'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ProtectedRoute>
                  <EditProfilePage />
                </ProtectedRoute>
              </Suspense>
            }
          />
          {/* Settings: */}
          <Route
            path='/settings'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <SettingsPage />
              </Suspense>
            }
          />
          {/* Beta Testing: */}
          <Route
            path='/beta-testing'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ProtectedRoute>
                  <BetaPage />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path='/feedback/:id'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <FeedbackDetailPage />
              </Suspense>
            }
          />
          {/* Edit Project: */}
          <Route
            path='/projects/edit/:id'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ProtectedRoute>
                  <NewProjectPage />
                </ProtectedRoute>
              </Suspense>
            }
          />
          {/* Invite Friends: */}
          <Route
            path='/projects/:id/invite'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ProtectedRoute>
                  <ComingSoonPage feature="Friend Invitations" />
                </ProtectedRoute>
              </Suspense>
            }
          />
          {/* Events: */}
          <Route
            path='/events'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ComingSoonPage feature="Events" />
              </Suspense>
            }
          />
          <Route
            path='/events/create'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ComingSoonPage feature="Event Creation" />
              </Suspense>
            }
          />
          <Route
            path='/events/:id'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <ComingSoonPage feature="Event Details" />
              </Suspense>
            }
          />
          {/* Design System: */}
          <Route
            path='/design-system'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={true} />}>
                <DesignSystemPage />
              </Suspense>
            }
          />
          <Route
            path='/share'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <SharePage />
              </Suspense>
            }
          />
          <Route
            path='/terms-of-service'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <TermsOfService />
              </Suspense>
            }
          />
          <Route
            path='/privacy-policy'
            element={
              <Suspense fallback={<LoadingSpinner hasBackground={false} />}>
                <PrivacyPolicy />
              </Suspense>
            }
          />
        </Route>
        {/* -------------------------- Layout End -------------------------- */}
        {/* Catch-all for 404: */}
        <Route
          path='*'
          element={
            <Suspense fallback={<LoadingSpinner hasBackground={true} />}>
              <ComingSoonPage feature="404 - Page Not Found" />
            </Suspense>
          }
        />
      </Routes>
    );
  }

export default Router
